import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";
import cookies from "js-cookie";
import KPIChartCard from "./KPIChartCard";

const pct1 = (n) => (Number.isFinite(+n) ? `${(+n).toFixed(1)}%` : "—");

export default function TinyLineChartBorrower() {
  const getIsMobile = () =>
    typeof window !== "undefined" ? window.innerWidth <= 1200 : false;
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [rows, setRows] = useState([]);
  const [delta, setDelta] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const response = await fetch(
        "https://www.smart-lend.com/api/dashboard/monthlyInterestRate",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookies.get("token")}`,
          },
        }
      );
      const json = await response.json();

      if (!alive) return;

      const all = Array.isArray(json["All Interest Rate (Current Month)"])
        ? json["All Interest Rate (Current Month)"]
        : [];

      const cur = +json["Current Month Average Interest Rate"] || 0;
      const prev = +json["Previous Month Average Interest Rate"] || 0;

      setRows(
        all.map((x, i) => ({
          idx: i + 1,
          interest_rate: Number.isFinite(+x.interest_rate)
            ? +(x.interest_rate * 100).toFixed(2)
            : 0,
        }))
      );

      setDelta(((cur - prev) * 100).toFixed(1));
    })();

    return () => {
      alive = false;
    };
  }, []);

  const isEmpty = rows.length === 0;

  const avg = useMemo(() => {
    if (!rows.length) return null;
    const s = rows.reduce((acc, r) => acc + (r.interest_rate || 0), 0);
    return s / rows.length;
  }, [rows]);

  const pill = delta === null ? "This month" : +delta >= 0 ? `▲ ${delta}%` : `▼ ${Math.abs(+delta)}%`;

  return (
    <KPIChartCard
      title="Monthly Interest Rate"
      pill={pill}
      value={pct1(avg)}
      subtitle={isEmpty || isMobile ? "" : "Current month average"}
      footer={isEmpty || isMobile ? "" : "Hover for daily points"}
      isEmpty={isEmpty}
      emptyTitle="No rate history"
      emptySub="Interest rate trend will appear once we have data this month."
    >
      <div style={{ width: "100%", height: isMobile ? 60 : 90 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <Line
              type="monotone"
              dataKey="interest_rate"
              stroke="var(--br-primary, #7057f6)"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Tooltip
              wrapperStyle={{ outline: "none" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div
                    style={{
                      background: "var(--br-surface, rgba(255,255,255,0.96))",
                      border: "1px solid var(--br-border, rgba(148,163,184,0.20))",
                      borderRadius: 12,
                      padding: "8px 10px",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 800,
                      fontSize: 12,
                      color: "var(--br-text, rgba(15,23,42,0.75))",
                    }}
                  >
                    Interest: {pct1(payload[0].value)}
                  </div>
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </KPIChartCard>
  );
}
